// Client for the global column-rename dictionary (Tools > Column Renamer) and
// the module-level case-map signal the SQL-editor autoformat reads to re-case
// typed columns to their canonical spelling.
import { createSignal } from 'solid-js';
import { api } from './api.js';

// Harvest distinct CASE-SENSITIVE column names from a connection (or one
// database). Admin only. database '' / undefined ⇒ whole connection.
export async function harvestColumns(connectionId, database) {
  // connectionId comes from a <select> (a string); the Go handler wants int64.
  const r = await api.post('/api/tools/column-rename/harvest', { connectionId: Number(connectionId), database: database || '' });
  return r.columns || [];
}

// Load the raw dictionary pairs [{match, canonical}] (any user).
export async function loadRenameMapPairs() {
  const r = await api.get('/api/tools/column-rename/map');
  return r.pairs || [];
}

// Merge (upsert) pairs into the dictionary (admin only). Returns { ok, saved }.
export async function saveRenameMap(pairs) {
  return api.put('/api/tools/column-rename/map', { pairs });
}

// Generate the bulk CHANGE COLUMN statements for the SAVED dictionary against a
// connection (or one database). Admin only. Returns { statements, count, tables }.
export async function previewRenames(connectionId, database) {
  return api.post('/api/tools/column-rename/preview', { connectionId: Number(connectionId), database: database || '' });
}

// Execute the (reviewed/edited) rename statements on a connection. Admin only.
// onError: 'stop' | 'continue'. Returns { results, ran, failed, ok }.
export async function executeRenames(connectionId, statements, onError) {
  return api.post('/api/tools/column-rename/execute', { connectionId: Number(connectionId), statements, onError });
}

// ── Global case-map for the editor autoformat ─────────────────────────────────
// lowercased(match) → canonical spelling. null until first loaded. Shared by
// every CodeMirrorSQL instance via the columnsFacet, so a save flows everywhere.
const [columnCaseMap, setColumnCaseMap] = createSignal(null);
export { columnCaseMap };

export async function loadColumnCaseMap() {
  try {
    const pairs = await loadRenameMapPairs();
    const m = new Map();
    for (const p of pairs) {
      if (p && p.match && p.canonical) m.set(String(p.match).toLowerCase(), String(p.canonical));
    }
    setColumnCaseMap(m);
    return m;
  } catch {
    return columnCaseMap(); // keep whatever we had on a transient failure
  }
}

export function resetColumnCaseMap() { setColumnCaseMap(null); }
